import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NavigationTrackerService {
  private isInitialNavigation = true;
  private readonly STORAGE_KEY = 'currentRoute';

  /**
   * Routes that are publicly accessible and should NEVER be
   * stored as the "last known route" or used to block navigation.
   */
  private readonly PUBLIC_ROUTES = new Set([
    '/',
    '/login',
    '/register',
    '/forgotpassword',
    '/terms-and-conditions',
  ]);

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        this.isInitialNavigation = false;

        const url = event.urlAfterRedirects.split('?')[0]; // strip query params for matching

        if (this.isPublicRoute(url)) {
          // User has landed on a public page (e.g. after logout to /login).
          // Clear the stored route so no stale protected route is remembered.
          sessionStorage.removeItem(this.STORAGE_KEY);
        } else {
          // Only store protected/app routes.
          sessionStorage.setItem(this.STORAGE_KEY, event.urlAfterRedirects);
        }
      });
    }
  }

  /**
   * Call this explicitly in your logout flow BEFORE navigating to /login.
   * Ensures no stale route survives into the next session.
   */
  clearStoredRoute(): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Returns true when the user manually changed the URL (copy-paste / edit).
   * Manual URL change causes a full page reload so Angular bootstraps fresh
   * and isInitialNavigation is true. If the target URL differs from the
   * stored route, the user pasted a different URL → block it.
   */
  shouldBlockNavigation(targetUrl: string): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    // Normal in-app navigation — never block.
    if (!this.isInitialNavigation) {
      return false;
    }

    const storedRoute = sessionStorage.getItem(this.STORAGE_KEY);

    // No stored route means fresh session or post-logout — allow freely.
    if (!storedRoute) {
      return false;
    }

    // If the stored route itself was a public route (shouldn't happen now,
    // but defensive check), don't use it as a blocker.
    if (this.isPublicRoute(storedRoute.split('?')[0])) {
      return false;
    }

    const normalizedTarget = targetUrl.replace(/\/$/, '') || '/';
    const normalizedStored = storedRoute.replace(/\/$/, '') || '/';

    // Same URL = page refresh — allow. Different URL = manual change — block.
    return normalizedTarget !== normalizedStored;
  }

  getStoredRoute(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return sessionStorage.getItem(this.STORAGE_KEY);
  }

  private isPublicRoute(url: string): boolean {
    // Exact match against known public routes.
    return this.PUBLIC_ROUTES.has(url);
  }
}